/**
 * Input surface — the DOM-facing element that owns pointer and
 * keyboard listeners on the match screen.
 *
 * All translation logic lives in `mapper.ts` (pure); this component
 * bridges the DOM to it. #563: WASD is the ONE player movement scheme
 * (click-to-move/follow-cursor were removed outright):
 *
 *   • right-click-hold (button 2) → FORWARD movement (#752). A second,
 *     independent source for the SAME `forward` bit the `w` key drives — the
 *     two are OR-ed, never overwritten (see `rmbForwardRef` below). It does
 *     NOT steer (#565: the aim-lock this used to drive, `face`, is retired —
 *     facing continuously tracks the cursor via the unconditional `aimPoint`
 *     stream below) and it does NOT target (only button 0 does).
 *   • keydown/keyup w/a/s/d → held-state → `moveDir` sent on every
 *     TRANSITION (not every tick — the sim persists the intent and resolves
 *     it every tick on its own). #785: two DIRECTLY OPPOSING keys held at once
 *     (w+s, or a+d) no longer cancel to a dead stop — the more recently
 *     pressed one wins on that axis (see `resolveOpposing` below).
 *   • pointerdown left (button 0) → hit-test → setTarget
 *   • keydown (document, guarded by route + not-typing-in-input) → mapper
 *   • #752: suppress contextmenu at the DOCUMENT level (not per-element), so
 *     right-clicking an ability icon / unit frame / any other HUD element
 *     never opens the browser menu mid-fight. Gated by the same two guards
 *     `onKeyDown` uses (in-match route + not typing in a text field) so a
 *     chat/settings input keeps its native copy/paste menu.
 *
 * #565: the canonical cursor/aim primitive. `cursorTracker.ts`'s
 * `startAimPointStream` is started ONCE on mount and streams the current
 * cursor's world position EVERY TICK, unconditionally — independent of
 * button state or any toggle. This retires BOTH the old `face` input
 * (WASD's button-gated aim-lock, above) AND the #346 "always face mouse"
 * toggle (its entire purpose — conditionally sending `face` from a bare
 * pointer move — no longer exists once facing always tracks the cursor).
 * `Entity.facing` is derived from the resulting `Entity.aimPoint`
 * server-side (`applyAimPointFacing` in `@pit/sim`'s `step.ts`) — this
 * component only collects + sends the raw point.
 *
 * The surface is a full-viewport transparent div positioned above the
 * canvas but BELOW the HUD overlay elements — the HUD gets its own
 * pointer events by being on top with higher z-index.
 */

import { useEffect, useRef, type ReactElement } from "react";
import {
  toAbilitySlot,
  type AbilitySlot,
  type TargetType,
  type WireSimState,
} from "@pit/protocol";
import { useStore } from "../store/context.js";
import { useWsClient } from "../net/WsClientProvider.js";
import {
  buildReverseIndex,
  normalizeChord,
  chordId,
  useKeymap,
} from "./keybinding/index.js";
import { useWasdStrafeMode } from "./wasdStrafeMode.js";
import {
  buildMoveDirInput,
  mapKeyEvent,
  mapPointerToSetTarget,
  mapResultInputs,
  wasdDirectionForKey,
  NO_WASD_HELD,
  type CanvasPoint,
  type MapperContext,
  type WasdDirection,
  type WasdHeldState,
  NO_MESSAGE,
} from "./mapper.js";
import { TickThrottle } from "./throttle.js";
import { getLastCanvasPoint, setLastCanvasPoint, startAimPointStream } from "./cursorTracker.js";
import type { ProjectionCenter } from "../render/index.js";
import { DEFAULT_HUD_CHAMPION_ID } from "../champions/abilityLabels.js";
import { resolveVisualSlotToCanonical } from "../champions/abilityLayout.js";
import { abilityTargetType } from "../champions/abilityTargetType.js";
import { reportCastError } from "./castability.js";
import { SpellQueue, type CastInput } from "./spellQueue.js";
import { loadSpellQueueWindowMs } from "../prefs.js";

const TICK_RATE_HZ = 30;

/** #785: which key of an opposing pair was pressed most recently. `null` until
 *  either has been pressed (and after a blur reset). */
type VerticalWinner = "forward" | "backward";
type HorizontalWinner = "strafeLeft" | "strafeRight";

/**
 * #785: resolve the two DIRECTLY OPPOSING WASD pairs — the more recently
 * pressed key wins its axis outright instead of the two cancelling to a dead
 * stop (hold w, press s ⇒ reverse, not stop).
 *
 * Takes PHYSICAL held-state (which keys are actually down, incl. #752's RMB
 * forward source) and returns the state the wire message is built from. Only a
 * both-held pair is touched: the loser's bit is dropped for as long as both are
 * down, and because the physical state is still tracked in full, RELEASING the
 * winner falls straight back to the still-held loser (release s ⇒ w resumes).
 * Non-opposing combinations (w+d) are passed through untouched and still hit
 * the sim's diagonal speed clamp.
 *
 * A `null` winner with both keys down is unreachable in practice (every press
 * that can produce a both-held pair records itself as that axis's winner) and
 * degrades to the pre-#785 cancel-to-zero, which the sim still implements as a
 * defensive fallback (`directionalDelta`).
 *
 * Pure, and keyed off `WasdDirection` rather than physical key codes, so
 * keybind remapping (#611) carries it over unchanged.
 */
function resolveOpposing(
  held: WasdHeldState,
  verticalWinner: VerticalWinner | null,
  horizontalWinner: HorizontalWinner | null,
): WasdHeldState {
  const bothVertical = held.forward && held.backward;
  const bothHorizontal = held.strafeLeft && held.strafeRight;
  if (!bothVertical && !bothHorizontal) return held;
  return {
    forward: bothVertical ? verticalWinner === "forward" : held.forward,
    backward: bothVertical ? verticalWinner === "backward" : held.backward,
    strafeLeft: bothHorizontal ? horizontalWinner === "strafeLeft" : held.strafeLeft,
    strafeRight: bothHorizontal ? horizontalWinner === "strafeRight" : held.strafeRight,
  };
}

export interface InputSurfaceProps {
  /** Live camera center getter — read per pointer event so hit-testing
   *  tracks the follow camera (which moves every frame). */
  readonly getProjectionCenter: () => ProjectionCenter;
}

export function InputSurface({ getProjectionCenter }: InputSurfaceProps): ReactElement {
  const { state, dispatch } = useStore();
  const client = useWsClient();
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const castThrottle = useRef(new TickThrottle(TICK_RATE_HZ));

  // #797: the single-slot spell-queue buffer (depth 1, replace-not-stack).
  // The window is read ONCE per mount from prefs and clamped to [0, 210] ms
  // there; 0 disables the feature outright (the kill switch), leaving this
  // whole path byte-identical to pre-#797 behaviour.
  const spellQueue = useRef(new SpellQueue(loadSpellQueueWindowMs()));
  // Camera center is read lazily per event (the follow camera moves
  // every frame; a captured value would go stale immediately).
  const getCenterRef = useRef(getProjectionCenter);
  getCenterRef.current = getProjectionCenter;

  // Context builder — reads latest state via ref so listeners don't
  // capture stale snapshots.
  // Live keymap + input-suppression from the shared store (#70). The keymap
  // updates immediately after a rebind (no reload); suppression is held while
  // the settings UI is open so rebind keystrokes never reach the sim. Read via
  // refs so the run-once keydown listener always sees the latest values.
  const { keymap, inputSuppressed } = useKeymap();
  const keymapRef = useRef(keymap);
  keymapRef.current = keymap;
  const suppressedRef = useRef(inputSuppressed);
  suppressedRef.current = inputSuppressed;

  // #351: live WASD strafe-axis preference. Read via a ref so the run-once
  // keydown/keyup listeners always see the latest value (same pattern as
  // keymap/suppressed above) and stamp it onto every outgoing `moveDir`
  // message — the sim resolves the actual axis, this is purely "which
  // preference is currently selected".
  const { wasdStrafeMode } = useWasdStrafeMode();
  const wasdStrafeModeRef = useRef(wasdStrafeMode);
  wasdStrafeModeRef.current = wasdStrafeMode;

  // #252: WASD held-key state, persisted across renders (one entry per
  // direction). `NO_WASD_HELD` (all-false) is the natural rest state.
  // This is the KEYBOARD source ONLY — the RMB source below must never
  // write it (see `mergedHeld`).
  const heldRef = useRef<WasdHeldState>(NO_WASD_HELD);

  // #752: right-click-hold is a SECOND, independent source for the SAME
  // `forward` bit `w` drives. Kept in its own ref rather than folded into
  // `heldRef` because a shared boolean makes the two sources FIGHT: an
  // RMB-up writing `forward: false` would also stop a player who still
  // physically holds `w` (and vice versa). Two sources, OR-ed at send time.
  const rmbForwardRef = useRef(false);

  // #785: the most recent press on each opposing axis — the ONLY place press
  // ORDER is known (the wire carries four unordered booleans, so the sim
  // cannot resolve this). Written by every input that engages a direction
  // (keydown, and #752's RMB-down for `forward`); never cleared on release,
  // because a winner is only ever consulted while BOTH keys of its pair are
  // physically down, and reaching that state always re-stamps it.
  const verticalWinnerRef = useRef<VerticalWinner | null>(null);
  const horizontalWinnerRef = useRef<HorizontalWinner | null>(null);

  /** Record the axis winner for a direction just engaged (#785). */
  const noteMostRecentPress = (dir: WasdDirection): void => {
    if (dir === "forward" || dir === "backward") {
      verticalWinnerRef.current = dir;
    } else {
      horizontalWinnerRef.current = dir;
    }
  };

  /** The state the wire message is built from: keyboard held-state OR the
   *  RMB forward source (#752), with each opposing pair resolved to its most
   *  recent press (#785). Resolution happens HERE, not at `buildMoveDirInput`,
   *  so `sendMoveDirIfChanged`'s transition comparison operates on the RESOLVED
   *  output — comparing physical state would send on a press that resolves to
   *  the same message (redundant) and skip one that resolves to a different
   *  one (missing). */
  const mergedHeld = (): WasdHeldState => {
    const physical =
      rmbForwardRef.current && !heldRef.current.forward
        ? { ...heldRef.current, forward: true }
        : heldRef.current;
    return resolveOpposing(physical, verticalWinnerRef.current, horizontalWinnerRef.current);
  };

  /**
   * Send `moveDir` iff the MERGED state actually changed (#752). Every
   * movement transition routes through here so the "send on TRANSITION, not
   * every tick" contract holds per-EFFECT rather than per-source: one source
   * engaging a direction the other already holds is not a transition the sim
   * needs to hear about (no redundant identical message), and one source
   * releasing a direction the other still holds must not send a spurious
   * stop. Callers snapshot `mergedHeld()` BEFORE mutating their source ref
   * and pass it as `before`.
   *
   * Only touches refs + the stable `client`, so the run-once listener effect
   * capturing the first-render instance is safe (same as every other handler
   * in this file).
   */
  const sendMoveDirIfChanged = (before: WasdHeldState): void => {
    const after = mergedHeld();
    if (
      before.forward === after.forward &&
      before.backward === after.backward &&
      before.strafeLeft === after.strafeLeft &&
      before.strafeRight === after.strafeRight
    ) {
      return;
    }
    client.sendInput(buildMoveDirInput(after, wasdStrafeModeRef.current));
  };

  /** Shared "user is typing" guard — the keydown listener and the #752
   *  document-level contextmenu suppressor use the SAME test so a text field
   *  keeps both its keystrokes and its native right-click menu. */
  const isTypingTarget = (target: EventTarget | null): boolean => {
    if (target === null || !(target instanceof HTMLElement)) return false;
    const tag = target.tagName;
    return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
  };

  const buildContext = (): MapperContext => ({
    snapshot: stateRef.current.hudSnapshot,
    // #731: never coerce an unknown seat to 0 — `MapperContext.ownSeat` is
    // nullable precisely so the seat-relative target cases (mapper.ts) can
    // no-op until the real seat is known, instead of piloting seat 0.
    ownSeat: stateRef.current.ownSeat,
    projectionCenter: getCenterRef.current(),
    keymap: keymapRef.current,
    // #565: the shared cursor primitive — the SAME last-known canvas point
    // `ActionBar`'s click-to-cast aim already reads via `getLastCanvasPoint`.
    lastPointer: getLastCanvasPoint(),
    // #417/#530: remap a keybind's VISUAL bar slot to the LOCAL PLAYER'S
    // current champion's saved layout AND current loadout selection — the
    // exact same single resolver `ActionBar`'s rendering and `LoadoutPicker`'s
    // slot-layout editor call, so a keypress can never cast a different
    // ability than the one visually displayed at that bar position (#530 —
    // this `state.ownLoadout` reference was the literal missing line that let
    // the pre-#530 raw `order[visualSlot]` lookup diverge from `ActionBar`).
    // Falls back to `DEFAULT_HUD_CHAMPION_ID` pre-pick, mirroring every other
    // `ownChampionId ?? DEFAULT_HUD_CHAMPION_ID` call site (`ActionBar`'s own
    // fallback convention). `null` (an empty visual position — nothing to
    // cast there) is passed straight through; `actionToIntent` no-ops rather
    // than guess a fallback canonical slot.
    resolveActionSlot: (visualSlot: AbilitySlot): AbilitySlot | null => {
      const championId = stateRef.current.ownChampionId ?? DEFAULT_HUD_CHAMPION_ID;
      const canonical = resolveVisualSlotToCanonical(
        championId,
        stateRef.current.ownLoadout,
        visualSlot as unknown as number,
      );
      return canonical === null ? null : toAbilitySlot(canonical);
    },
    // #815: the CANONICAL slot's authored target type, from the same
    // content-derived table `ActionBar`'s range coloring reads. Only
    // `"enemy"` unlocks auto-acquire; every other type (and an unknown slot,
    // `null`) leaves the press byte-identical to pre-#815.
    resolveTargetType: (canonicalSlot: AbilitySlot): TargetType | null => {
      const championId = stateRef.current.ownChampionId ?? DEFAULT_HUD_CHAMPION_ID;
      return abilityTargetType(championId, canonicalSlot as unknown as number);
    },
  });

  /**
   * #797: the freshest snapshot available for TIMING decisions — the raw
   * 30 Hz source, not `state.hudSnapshot`, which the HUD sampler refreshes at
   * only 10 Hz. A snapshot up to 3 ticks stale would make a 3-tick queue
   * window meaningless (the whole feature is single-tick-precision timing).
   * Falls back to the sampled HUD snapshot when the source is empty (demo /
   * test harnesses that seed the store directly).
   */
  const freshestSnapshot = (): WireSimState | null =>
    client.snapshotSource.latestSnapshot() ?? stateRef.current.hudSnapshot;

  /**
   * #797: the ONE cast-send path — a live keypress and a queued release both
   * go through here, so the #787 out-of-resource prediction fires against the
   * snapshot current AT SEND TIME (a queued cast's resource state is judged
   * when it actually goes out, not when it was pressed).
   */
  const sendCast = (input: CastInput): void => {
    // #795: NO ability SFX here. Cast-time audio was speculative — it fired on
    // a press the sim might fizzle (out of resource, on cooldown, out of
    // range) and, for anything with a cast time or travel time, at the wrong
    // moment. It now fires on IMPACT, off the snapshot's `damage` events
    // (`sound/impactAudio.ts`). Deliberately NOT replaced with a cast-start
    // cue: that is a separate, deferred piece of design.
    // #787: locally-predicted castability feedback — an out-of-resource press
    // pops a centre-screen "Not enough energy" notice instead of failing
    // silently. The SEND still happens either way (the sim is authoritative;
    // this prediction never gates input). `dispatch` is referentially stable
    // (`useReducer`), like `client`, so the run-once listener effect can
    // capture it safely.
    reportCastError(dispatch, {
      snapshot: stateRef.current.hudSnapshot,
      ownSeat: stateRef.current.ownSeat,
      championId: stateRef.current.ownChampionId ?? DEFAULT_HUD_CHAMPION_ID,
      slot: input.slot,
    });
    client.sendInput(input);
  };

  const pointFromEvent = (
    surface: HTMLDivElement,
    clientX: number,
    clientY: number,
  ): CanvasPoint => {
    const r = surface.getBoundingClientRect();
    return { x: clientX - r.left, y: clientY - r.top };
  };

  // #565: the unconditional per-tick `aimPoint` wire stream — started ONCE
  // per mount, stopped on unmount. `cursorTracker.ts` owns the collection,
  // conversion, AND wire-send; this is the sole call site that starts it.
  useEffect(() => {
    const stream = startAimPointStream({
      send: (input) => client.sendInput(input),
      getProjectionCenter: () => getCenterRef.current(),
    });
    return () => stream.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #797: the spell-queue FLUSH driver. Subscribed to the raw 30 Hz snapshot
  // fan-out (not the 10 Hz HUD sampler — see `freshestSnapshot`), so a held
  // press is re-judged on EVERY tick that arrives and goes out on the first
  // one whose gates are open. `WsSnapshotSource` fans out to every live
  // subscriber, so this coexists with the renderer's own subscription.
  // Started ONCE per mount, disposed on unmount — same lifecycle as the
  // `aimPoint` stream above; the callback only touches refs plus the stable
  // `client`/`dispatch`.
  useEffect(() => {
    const handle = client.snapshotSource.subscribe((entry) => {
      const due = spellQueue.current.poll(entry.state, stateRef.current.ownSeat);
      if (due === null) return;
      sendCast(due);
    });
    return () => handle.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pointer handlers — attached directly on the surface (not document).
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    const surface = surfaceRef.current;
    if (surface === null) return;
    const point = pointFromEvent(surface, e.clientX, e.clientY);
    const ctx = buildContext();
    if (e.button === 2) {
      // Pointer capture: what guarantees the matching `pointerup` still
      // arrives here when the cursor leaves the surface mid-hold.
      surface.setPointerCapture(e.pointerId);
      // #752: RMB-hold drives FORWARD movement (the standard MMO/ARPG
      // right-click-to-run default). Deliberately NOT a targeting or aiming
      // input — facing already tracks the cursor every tick (#565), and only
      // button 0 targets. Guards mirror the WASD keydown path exactly:
      // suppressed (settings UI open) → nothing reaches the sim; gated to
      // `active` so a pre-match hold queues nothing.
      if (suppressedRef.current) return;
      if (stateRef.current.hudSnapshot?.phase !== "active") return;
      if (rmbForwardRef.current) return; // already held — not a transition
      const before = mergedHeld();
      rmbForwardRef.current = true;
      // #785: RMB-down is an explicit, more-recent `forward` input, so it wins
      // the vertical axis over an already-held `s` exactly as `w` would. The
      // #752 two-ref split still stands — the sources are never overwritten,
      // only ranked — so RMB-up falls back to whatever is still held, and a
      // later `s` press outranks a live RMB-hold.
      noteMostRecentPress("forward");
      sendMoveDirIfChanged(before);
    } else if (e.button === 0) {
      const res = mapPointerToSetTarget({ button: e.button, point }, ctx);
      if (res.kind === "intent") {
        client.sendInput(res.input);
      }
    }
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const surface = surfaceRef.current;
    if (surface === null) return;
    const point = pointFromEvent(surface, e.clientX, e.clientY);
    // #565: the SOLE writer of the shared cursor primitive (`cursorTracker`)
    // — `ActionBar`'s HUD range indicator, click-to-cast aim, keyboard-cast
    // aim (`ctx.lastPointer` above), AND the per-tick `aimPoint` wire stream
    // all read this same last-known canvas point.
    setLastCanvasPoint(point);
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    const surface = surfaceRef.current;
    if (surface !== null && surface.hasPointerCapture(e.pointerId)) {
      surface.releasePointerCapture(e.pointerId);
    }
    if (e.button !== 2) return;
    // #752: release the RMB forward source. Mirrors the WASD `onKeyUp`
    // contract: the source bit is cleared even while suppressed (never leave
    // a stuck hold), only the SEND is skipped. Clearing it is a no-op for the
    // wire whenever `w` is still held — `sendMoveDirIfChanged` sees an
    // unchanged merged state.
    if (!rmbForwardRef.current) return;
    const before = mergedHeld();
    rmbForwardRef.current = false;
    if (suppressedRef.current) return;
    sendMoveDirIfChanged(before);
  };

  // Keyboard: document-level, guarded by (in match screen) + (not typing).
  useEffect(() => {
    const inMatchRoute = (): boolean =>
      typeof window === "undefined" || window.location.hash.startsWith("#/match");

    /**
     * The cast half of `onKeyDown` (#797 queue + throttle + send). Split out
     * so the #815 two-intent path can run it for the `cast` element without
     * duplicating the queue/throttle rules.
     */
    const sendCastInput = (castInput: CastInput): void => {
      // Cast keypresses are edge-triggered, same pattern as the WASD
      // `!e.repeat` guard above (#710). OS/browser auto-repeat keydowns for a
      // held cast key fire roughly once per sim tick — indistinguishable in
      // cadence from the `TickThrottle` below — so without that guard (now in
      // `onKeyDown`, before anything is sent) a held key re-sends a fresh
      // `cast` input for as long as it's held. `e.repeat` is tracked by the
      // browser per PHYSICAL key, so this needs no extra held-state (unlike
      // WASD, which ORs multiple simultaneously-held directional keys): a
      // genuine keyup+keydown re-press (not OS repeat) always has
      // `repeat: false` and still fires every time, and switching to a
      // different cast key mid-hold is an unrelated physical keydown with its
      // own `repeat` flag — no cross-key state to leak.
      //
      // #797 SPELL QUEUE. Judged BEFORE the throttle below on purpose: a
      // press inside the window sends nothing NOW, so it must still be able
      // to REPLACE an already-held one (depth 1, last press wins) even when
      // several presses land inside a single tick interval — a throttle
      // check first would silently drop the later, winning press.
      // `press()` returns "sendNow" for everything it cannot prove is
      // early-but-close (legal now, a mis-press far from ready, feature off,
      // unknown state), so every other path below is untouched.
      if (
        spellQueue.current.press(
          castInput,
          freshestSnapshot(),
          stateRef.current.ownSeat,
        ) === "queued"
      ) {
        return;
      }
      // Belt-and-suspenders backstop: still throttled to one send per
      // tick interval even for genuine (non-repeat) rapid re-presses,
      // matching the sim's own tick cadence.
      const now = performance.now();
      if (!castThrottle.current.tryConsume(now)) return;
      sendCast(castInput);
    };

    const onKeyDown = (e: KeyboardEvent): void => {
      // Guard: settings UI open / capturing a rebind (#70). While suppressed,
      // no key reaches the sim — a rebind keystroke can't also fire an action.
      if (suppressedRef.current) return;
      // Guard: not typing in an input.
      if (isTypingTarget(e.target)) return;
      // #833 CHAT INPUT CAPTURE — the load-bearing correctness property of
      // in-game chat. While the chat box is open, NO key reaches gameplay:
      // typing "watch out" must not fire the abilities bound to w/a/t/c/h.
      //
      // This deliberately does NOT rely on the `isTypingTarget` guard above.
      // That one only fires when the event's target IS the chat <input>, so
      // it silently stops protecting the player the instant focus drifts
      // (a stray click on the canvas, a browser focus quirk) — and "my
      // abilities fired while I was typing" is exactly the bug class that
      // gets someone killed. This guard is state-driven, not focus-driven,
      // so it holds regardless of where the keystroke was aimed.
      //
      // Placed BEFORE the route check and before WASD so it covers every
      // downstream branch — including the `openChat` dispatch below, which
      // is why re-pressing Enter while open never re-opens: the chat box
      // itself owns Enter-to-send from here on.
      if (stateRef.current.chatOpen) return;
      // Guard: only in match route (hash-based). Cheap check that
      // avoids the router hook to keep the listener lifecycle simple.
      if (!inMatchRoute()) return;

      // #252/#563: WASD intercepts w/a/s/d BEFORE the ordinary rebindable
      // keymap dispatch — held-key movement, not an edge-triggered action.
      // Gated to `active` (a pre-match key-hold never queues a wasted send —
      // the server would drop it anyway (`moveDir`/`aimPoint` are
      // `active`-only inputs), but there's no reason to send it).
      if (stateRef.current.hudSnapshot?.phase === "active") {
        const dir = wasdDirectionForKey(e.key);
        if (dir !== null) {
          e.preventDefault();
          if (!e.repeat && !heldRef.current[dir]) {
            const before = mergedHeld();
            heldRef.current = { ...heldRef.current, [dir]: true };
            // #785: this press is now its axis's most recent one — must be
            // stamped BEFORE the send so `mergedHeld()`'s `after` reflects it.
            // OS auto-repeat never gets here (the `!e.repeat` guard above), so
            // holding the losing key cannot re-claim the axis.
            noteMostRecentPress(dir);
            sendMoveDirIfChanged(before);
          }
          return;
        }
      }

      // Prevent default (e.g. Tab focus-cycling) for any key bound to a game
      // action, resolved through the live keymap rather than a hardcoded key.
      const chord = normalizeChord(e.key, {
        ctrl: e.ctrlKey,
        alt: e.altKey,
        shift: e.shiftKey,
        meta: e.metaKey,
      });
      const boundAction = buildReverseIndex(keymapRef.current).get(chordId(chord));
      if (boundAction !== undefined) {
        e.preventDefault();
      }
      // #833: `openChat` is the one `GameAction` with no wire intent — it
      // flips client-local UI state, which the pure mapper (correctly) has no
      // access to. Handled here, resolved through the LIVE KEYMAP like every
      // other action (never a hardcoded `e.key === "Enter"`), so it stays
      // rebindable and conflict-checked. Edge-triggered: an auto-repeat
      // keydown must not re-open (and thereby re-clear) the box.
      if (boundAction?.kind === "openChat") {
        if (!e.repeat) dispatch({ type: "chat.setOpen", open: true });
        return;
      }
      const ctx = buildContext();
      const res = mapKeyEvent(
        {
          key: e.key,
          repeat: e.repeat,
          ctrl: e.ctrlKey,
          alt: e.altKey,
          shift: e.shiftKey,
          meta: e.metaKey,
        },
        ctx,
      );
      if (res === NO_MESSAGE || res.kind === "none") return;

      // #815: a press can now resolve to TWO intents — an auto-acquired
      // `setTarget` followed by the `cast` that needs it. `mapResultInputs`
      // is the one flattener; order is load-bearing (target before cast).
      const inputs = mapResultInputs(res);
      // Auto-repeat for a cast press is dropped as a WHOLE press (#710 — see
      // `sendCastInput` above) — checked before anything is sent so a held
      // key can't re-send the auto-acquired `setTarget` either.
      if (e.repeat && inputs.some((i) => i.kind === "cast")) return;

      for (const input of inputs) {
        if (input.kind !== "cast") {
          client.sendInput(input);
          continue;
        }
        sendCastInput(input);
      }
    };

    // #252: keyup releases a held WASD direction.
    // #833: DELIBERATELY NOT gated on `chatOpen`, unlike keydown. A player
    // who was running when they opened the chat box keeps running (correct —
    // chat must not stop you), and the release of that already-held key must
    // still be honoured or they would run forever. The keydown guard already
    // guarantees no NEW direction can be engaged while typing, so this can
    // only ever clear a bit that was set before the box opened. Same
    // "release even while suppressed" contract the settings-overlay path
    // (`suppressedRef`) has had since #70.
    const onKeyUp = (e: KeyboardEvent): void => {
      const dir = wasdDirectionForKey(e.key);
      if (dir === null) return;
      if (!heldRef.current[dir]) return; // already released — no-op
      const before = mergedHeld();
      heldRef.current = { ...heldRef.current, [dir]: false };
      if (suppressedRef.current) return; // settings UI open — don't reach the sim
      // #752: a no-op on the wire when RMB still holds `forward` — the merged
      // state is unchanged, so releasing `w` mid-RMB-hold keeps running.
      sendMoveDirIfChanged(before);
    };

    // #252: alt-tab / window-blur safety net — a key physically released
    // while the tab is unfocused never fires a DOM keyup, which would
    // otherwise leave the entity walking forever. Reset + notify the sim.
    // #752: RMB is the same hazard (a button released while unfocused fires
    // no `pointerup`), so the net covers BOTH sources — the emptiness check
    // reads the merged state and the reset clears the RMB source too.
    const onBlur = (): void => {
      const held = mergedHeld();
      if (!held.forward && !held.backward && !held.strafeLeft && !held.strafeRight) return;
      heldRef.current = NO_WASD_HELD;
      rmbForwardRef.current = false;
      // #785: nothing is held any more, so no axis has a winner to remember.
      verticalWinnerRef.current = null;
      horizontalWinnerRef.current = null;
      client.sendInput(buildMoveDirInput(NO_WASD_HELD, wasdStrafeModeRef.current));
    };

    // #752: document-level contextmenu suppression. Per-element suppression
    // (the surface's old `onContextMenu` prop, now dropped as redundant) could
    // only ever cover the surface itself — the HUD renders ABOVE it, so a
    // right-click on an ability icon or unit frame never reached that handler
    // and the browser menu opened mid-fight. Suppressing here means no future
    // HUD component has to remember to opt in. Same two guards as `onKeyDown`:
    // gameplay-screen only, and never over a text field (a chat/settings input
    // must keep its native copy/paste menu). Deliberately NOT gated on
    // `suppressedRef` — the settings UI's own text fields are already exempt
    // via the typing guard, and its non-text chrome should not spawn a browser
    // menu either.
    const onContextMenu = (e: MouseEvent): void => {
      if (isTypingTarget(e.target)) return;
      if (!inMatchRoute()) return;
      e.preventDefault();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup", onKeyUp);
    document.addEventListener("contextmenu", onContextMenu);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keyup", onKeyUp);
      document.removeEventListener("contextmenu", onContextMenu);
      window.removeEventListener("blur", onBlur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={surfaceRef}
      data-testid="input-surface"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      style={{
        position: "absolute",
        inset: 0,
        // Zero background so the canvas below shows through.
        touchAction: "none",
        // Only capture pointer events during live gameplay. Outside
        // countdown/active (i.e. waiting + ended) this full-viewport div
        // would otherwise sit over the WaitingPanel / WinnerBanner and eat
        // clicks on their buttons (e.g. "Play again") — which read as "can't
        // click / can't re-queue". Let those UIs receive pointer events.
        pointerEvents:
          state.phase.phase === "countdown" || state.phase.phase === "active" ? "auto" : "none",
      }}
    />
  );
}
